import { z } from "zod";

import { fetchGatewayWithSessionRecovery } from "@/lib/auth/gateway";

import type { HubSpotRawOption } from "./types";

const HUBSPOT_DEFAULT_LOCALE = "es";
const PROVIDER_ROOT = "/api/hubspot/v1/provider";
const LOCALIZABLE_PATH_PREFIXES = ["/crm/v3/properties/", "/crm/v3/pipelines/"];

type QueryValue = string | number | boolean | ReadonlyArray<string> | undefined;
type ProviderHttpMethod = "GET" | "POST";

type HubSpotObjectIdInput = {
  id: string;
};

type HubSpotSearchFilter = {
  propertyName: string;
  operator: string;
  value?: string;
};

type HubSpotSearchBody = {
  filterGroups: ReadonlyArray<{
    filters: ReadonlyArray<HubSpotSearchFilter>;
  }>;
  sorts?: ReadonlyArray<string>;
  properties?: ReadonlyArray<string>;
  limit?: number;
  after?: string;
};

type HubSpotBatchReadBody = {
  inputs: ReadonlyArray<HubSpotObjectIdInput>;
  properties?: ReadonlyArray<string>;
  propertiesWithHistory?: ReadonlyArray<string>;
};

type ProviderRequestBody = HubSpotSearchBody | HubSpotBatchReadBody;

type ProviderRequest = {
  method?: ProviderHttpMethod;
  path?: string;
  qs?: Record<string, QueryValue>;
  body?: ProviderRequestBody;
};

type ProviderEnvelope = {
  method: ProviderHttpMethod;
  path: string;
  query: Record<string, string | string[]>;
  body?: ProviderRequestBody;
};

const rawOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  displayOrder: z.number().optional(),
  hidden: z.boolean().optional(),
});

const propertyMapSchema = z
  .record(z.string(), z.string().nullable())
  .transform((values) => {
    const normalized: Record<string, string> = {};
    for (const [name, value] of Object.entries(values)) {
      if (value !== null) {
        normalized[name] = value;
      }
    }
    return normalized;
  });

const providerObjectSchema = z.object({
  id: z.string(),
  properties: propertyMapSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  archived: z.boolean(),
});

const providerPageSchema = z.object({
  results: z.array(providerObjectSchema),
  paging: z
    .object({
      next: z.object({ after: z.string().optional() }).optional(),
    })
    .optional(),
});

const providerPropertySchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),
  fieldType: z.string(),
  description: z.string().optional(),
  groupName: z.string().optional(),
  options: z.array(rawOptionSchema).optional(),
  displayOrder: z.number().optional(),
  calculated: z.boolean().optional(),
  externalOptions: z.boolean().optional(),
  hasUniqueValue: z.boolean().optional(),
  hidden: z.boolean().optional(),
  hubspotDefined: z.boolean().optional(),
  formField: z.boolean().optional(),
});

const providerPropertyPageSchema = z.object({
  results: z.array(providerPropertySchema),
});

const providerPropertyResponseSchema = z.object({
  options: z.array(rawOptionSchema),
});

const providerAssociationIdSchema = z.union([z.string(), z.number()]).transform(String);

const providerAssociationPageSchema = z.object({
  results: z.array(
    z.object({
      toObjectId: providerAssociationIdSchema,
      associationTypes: z
        .array(
          z.object({
            category: z.string().optional(),
            label: z.string().nullable().optional(),
            typeId: z
              .union([z.number(), z.string().regex(/^\d+$/u)])
              .transform(Number)
              .optional(),
          }),
        )
        .optional(),
    }),
  ),
  paging: z
    .object({
      next: z.object({ after: z.string().optional() }).optional(),
    })
    .optional(),
});

const ownerTextSchema = z
  .string()
  .nullable()
  .optional()
  .transform((value) => value ?? "");

const providerOwnerSchema = z.object({
  id: z.string(),
  email: ownerTextSchema,
  firstName: ownerTextSchema,
  lastName: ownerTextSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  archived: z.boolean(),
});

const providerOwnerPageSchema = z.object({
  results: z.array(providerOwnerSchema),
  paging: z.object({
    next: z.object({ after: z.string().min(1) }).optional(),
  }).optional(),
});

const appendQuery = (url: URL, query: Record<string, QueryValue> | undefined): void => {
  if (!query) {
    return;
  }

  for (const [name, value] of Object.entries(query)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) url.searchParams.append(name, item);
    } else {
      url.searchParams.set(name, String(value));
    }
  }
};

const providerRequest = async (input: ProviderRequest): Promise<Response> => {
  const rawPath = input.path?.startsWith("/") ? input.path : `/${input.path ?? ""}`;
  const providerUrl = new URL(rawPath, "https://hubspot-provider.invalid");
  const path = providerUrl.pathname;
  const query: Record<string, QueryValue> = {};
  if (input.qs) {
    Object.assign(query, input.qs);
  }
  if (LOCALIZABLE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix)) && query.locale === undefined) {
    query.locale = HUBSPOT_DEFAULT_LOCALE;
  }
  appendQuery(providerUrl, query);

  const serializedQuery: Record<string, string | string[]> = {};
  for (const name of new Set(providerUrl.searchParams.keys())) {
    const values = providerUrl.searchParams.getAll(name);
    serializedQuery[name] = values.length === 1 ? values[0]! : values;
  }

  const requestBody: ProviderEnvelope = {
    method: input.method ?? "GET",
    path,
    query: serializedQuery,
  };
  if (input.body !== undefined) {
    requestBody.body = input.body;
  }

  return fetchGatewayWithSessionRecovery(PROVIDER_ROOT, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Correlation-Id": crypto.randomUUID(),
    },
    body: JSON.stringify(requestBody),
  });
};

const requestJson = async <T>(schema: z.ZodType<T>, input: ProviderRequest): Promise<T> => {
  const response = await providerRequest(input);
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(`HubSpot gateway failed: HTTP ${response.status}${detail ? ` - ${detail.slice(0, 200)}` : ""}`);
  }
  return schema.parse(await response.json());
};

const objectPage = (
  objectType: string,
  limit?: number,
  after?: string,
  properties?: ReadonlyArray<string>,
  archived?: boolean,
) =>
  requestJson(providerPageSchema, {
    path: `/crm/v3/objects/${objectType}`,
    qs: { limit, after, properties, archived },
  });

const objectById = (objectType: string, id: string | number, properties?: ReadonlyArray<string>) =>
  requestJson(providerObjectSchema, {
    path: `/crm/v3/objects/${objectType}/${encodeURIComponent(String(id))}`,
    qs: { properties },
  });

const batchRead = (objectType: string, body: HubSpotBatchReadBody) =>
  requestJson(providerPageSchema, {
    method: "POST",
    path: `/crm/v3/objects/${objectType}/batch/read`,
    body,
  });

const search = (objectType: string, body: HubSpotSearchBody) =>
  requestJson(providerPageSchema, {
    method: "POST",
    path: `/crm/v3/objects/${objectType}/search`,
    body,
  });

const associationsPage = (
  fromObjectType: string,
  fromObjectId: string,
  toObjectType: string,
  after?: string,
  limit?: number,
) =>
  requestJson(providerAssociationPageSchema, {
    path: `/crm/v4/objects/${fromObjectType}/${encodeURIComponent(fromObjectId)}/associations/${toObjectType}`,
    qs: { after, limit },
  });

export const getHubSpotClient = () => ({
  apiRequest: (input: ProviderRequest = {}) => providerRequest(input),
  crm: {
    associations: { v4: { basicApi: { getPage: associationsPage } } },
    properties: {
      coreApi: {
        getAll: (objectType: string) =>
          requestJson(providerPropertyPageSchema, {
            path: `/crm/v3/properties/${objectType}`,
            qs: { locale: HUBSPOT_DEFAULT_LOCALE },
          }),
      },
    },
    contacts: {
      basicApi: {
        getPage: (
          limit?: number,
          after?: string,
          properties?: ReadonlyArray<string>,
          _associations?: ReadonlyArray<string>,
          archived?: boolean,
          _propertiesWithHistory?: ReadonlyArray<string> | boolean,
        ) => objectPage("contacts", limit, after, properties, archived),
        getById: (id: string, properties?: ReadonlyArray<string>) =>
          objectById("contacts", id, properties),
      },
      batchApi: { read: (body: HubSpotBatchReadBody) => batchRead("contacts", body) },
      searchApi: { doSearch: (body: HubSpotSearchBody) => search("contacts", body) },
    },
    companies: {
      searchApi: { doSearch: (body: HubSpotSearchBody) => search("companies", body) },
    },
    deals: {
      basicApi: {
        getById: (id: string, properties?: ReadonlyArray<string>) =>
          objectById("deals", id, properties),
      },
      batchApi: { read: (body: HubSpotBatchReadBody) => batchRead("deals", body) },
      searchApi: { doSearch: (body: HubSpotSearchBody) => search("deals", body) },
    },
    lineItems: {
      batchApi: { read: (body: HubSpotBatchReadBody) => batchRead("line_items", body) },
    },
    products: {
      basicApi: {
        getPage: (
          limit?: number,
          after?: string,
          properties?: ReadonlyArray<string>,
          _associations?: ReadonlyArray<string>,
          archived?: boolean,
          _propertiesWithHistory?: ReadonlyArray<string> | boolean,
        ) => objectPage("products", limit, after, properties, archived),
      },
      batchApi: { read: (body: HubSpotBatchReadBody) => batchRead("products", body) },
      searchApi: { doSearch: (body: HubSpotSearchBody) => search("products", body) },
    },
    owners: {
      ownersApi: {
        getPage: (email?: string, after?: string, limit?: number, archived?: boolean) =>
          requestJson(providerOwnerPageSchema, {
            path: "/crm/v3/owners",
            qs: { email, after, limit, archived },
          }),
        getById: (id: number) =>
          requestJson(providerOwnerSchema, { path: `/crm/v3/owners/${id}` }),
      },
    },
  },
});

export const getHubSpotPropertyOptions = async (
  objectType: string,
  propertyName: string,
): Promise<HubSpotRawOption[]> => {
  const response = await providerRequest({
    path: `/crm/v3/properties/${objectType}/${propertyName}`,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `HubSpot property options ${objectType}/${propertyName} failed: HTTP ${response.status}${detail ? ` - ${detail.slice(0, 200)}` : ""}`,
    );
  }
  const payload = providerPropertyResponseSchema.parse(await response.json());
  return structuredClone(payload.options);
};
